/**
 * ApprovalTemplatePicker — fullscreen gallery overlay (style giống PresetGallery).
 * Click template → SlideDrawer trượt từ phải vào với form điền đơn.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - onSubmitted: (newApprovalId) => void
 *  - initialTemplateId?: string — pre-select & open drawer ngay
 *  - submitExtra?: object — khoá thêm gửi kèm POST /approvals (vd { link_task_id } khi gửi đơn TỪ một
 *    việc của quy trình: duyệt xong việc tự hoàn thành). Chỉ Core có — màn gọi tự lo chỉ truyền khi hệ hỗ trợ.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Search, FileCheck, Loader2, Send, ArrowLeft, AlertTriangle,
} from 'lucide-react';
import api from '@host/axios';
import toast from 'react-hot-toast';
import FieldInput, { isCrmFieldOff } from './builder/FieldInput';
import { LEAVE_INSUFFICIENT_MARKER } from './LeaveBalanceCard';
import { FIELD_TYPE_BY_VALUE, ensureSystemFields, isAssigneeSelfOnly, findAssigneeField, withKindAssigneeLock } from './builder/fieldRegistry';
import { evaluateVisible } from './builder/utils';
import { findDateIssues } from './builder/dateSanity';
import { getIconComponent, getColorClass } from './builder/IconPicker';
import SlideDrawer from '../common/SlideDrawer';
import { useAuth } from '@host/auth';

import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { i18n } from '../../i18n/runtime.js';

export default function ApprovalTemplatePicker({
  open, onClose, onSubmitted,
  initialTemplateId,
  initialKind,    // string — match template.kind, pick first matching
  initialPrefill, // object — merge vào formData khi pre-select
  submitExtra,    // object — khoá thêm của POST /approvals (xem đầu tệp)
}) {
  const {
    t
  } = useLingui();

  const { user: currentUser } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]); // loại phê duyệt (sort_order) — sắp nhóm khi gửi đơn
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(initialTemplateId || null);

  // Form state
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quota, setQuota] = useState(null); // { ok, unlimited?, used, limit, remaining, message } — giới hạn số lần/tháng

  // Lock body scroll khi gallery mở — tránh scroll bubbles lên page phía sau
  // (rubber-band trên Mac/iOS làm lộ inbox phía sau khi scroll cuối list).
  useEffect(() => {
    if (!open) return;
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    const orig = { overflow: document.body.style.overflow, paddingRight: document.body.style.paddingRight };
    document.body.style.overflow = 'hidden';
    if (scrollbarW > 0) document.body.style.paddingRight = `${scrollbarW}px`;
    return () => {
      document.body.style.overflow = orig.overflow;
      document.body.style.paddingRight = orig.paddingRight;
    };
  }, [open]);
  const [fieldErrors, setFieldErrors] = useState({}); // { [fieldId]: 'message' }

  // Tính initial formData khi chọn template — auto-fill assignee nếu setting bật
  const initialFormDataFor = (tpl) => {
    const data = {};
    if (currentUser?.id) {
      // Inject system fields trước khi tìm — template legacy có thể thiếu _assignee
      const assigneeField = ensureSystemFields(tpl?.form_fields || []).find(f => f.id === '_assignee');
      // auto_assign_requester !== false → mặc định ON
      if (assigneeField && assigneeField.enabled !== false && assigneeField.auto_assign_requester !== false) {
        data._assignee = currentUser.id;
      }
    }
    return data;
  };

  // Mẫu bật "Chỉ cho phép người gửi đơn" → ép người thực hiện = người đang đăng nhập,
  // bất kể pre-fill/initialPrefill/state cũ. BE chặn lần nữa (kernel approvalAssigneeLock).
  const forceSelfAssignee = (tpl, data) => {
    if (!currentUser?.id) return data;
    const fields = ensureSystemFields(tpl?.form_fields || []);
    if (!isAssigneeSelfOnly(fields, tpl?.kind)) return data;
    const f = findAssigneeField(fields);
    return { ...data, [(f && f.id) || '_assignee']: currentUser.id };
  };

  const loadTemplates = useCallback(() => {
    setLoading(true); setError(null);
    api.get('/approvals/templates', { params: { status: 'active', limit: 200 } })
      .then(r => setTemplates(r.data?.data || []))
      .catch(err => setError(err.response?.data?.message || err.message || t`Lỗi tải templates`))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { if (open) loadTemplates(); }, [open, loadTemplates]);
  // Load loại phê duyệt để sắp nhóm theo sort_order (màn gửi đơn mới).
  useEffect(() => {
    if (!open) return;
    api.get('/approvals/categories')
      .then(r => setCategories(r.data?.data || []))
      .catch(() => {});
  }, [open]);

  useEffect(() => { if (initialTemplateId) setSelectedId(initialTemplateId); }, [initialTemplateId]);

  // Auto-pick template theo initialKind (vd 'attendance_fix' từ attendance
  // card "Điều chỉnh") — rule user 2026-05-25. Chỉ pick khi chưa có
  // selectedId + templates đã load.
  useEffect(() => {
    if (!open || !initialKind || selectedId || templates.length === 0) return;
    const match = templates.find((t) => t.kind === initialKind);
    if (match) setSelectedId(match.id);
  }, [open, initialKind, selectedId, templates]);

  // Pre-fill form data khi templates load xong + có selectedId từ initial.
  // Merge initialPrefill (vd { _attendance_date: 'YYYY-MM-DD' }) vào initial data.
  useEffect(() => {
    if (selectedId && templates.length > 0 && Object.keys(formData).length === 0) {
      const tpl = templates.find(t => t.id === selectedId);
      if (tpl) {
        const data = initialFormDataFor(tpl);
        if (initialPrefill && typeof initialPrefill === 'object') {
          Object.assign(data, initialPrefill);
          // Map _attendance_date → field type='date' có _purpose='attendance_date'
          // hoặc field đầu tiên type='date' (fallback).
          const attDate = initialPrefill._attendance_date;
          if (attDate) {
            const fields = ensureSystemFields(tpl.form_fields || []);
            const dateField = fields.find(
              (f) => f.type === 'date' && (f._purpose === 'attendance_date' || f._purpose === 'work_date'),
            ) || fields.find((f) => f.type === 'date');
            if (dateField) data[dateField.id] = attDate;
          }
        }
        // Sau cùng: initialPrefill của caller không được ghi đè ô đang bị khoá.
        setFormData(forceSelfAssignee(tpl, data));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, templates.length, currentUser?.id, initialPrefill]);

  // Group templates by category → trả MẢNG [cat, tpls] đã SẮP theo sort_order của
  // loại phê duyệt (màn gửi đơn mới). Loại không có trong list → xếp cuối, alpha.
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? templates.filter(t => (t.name || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q))
      : templates;
    const groups = {};
    filtered.forEach(t => {
      const cat = t.category || i18n._(msg`Khác`);
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(t);
    });
    const orderMap = new Map(categories.map((c, i) => [c.name, c.sort_order ?? i]));
    return Object.entries(groups).sort((a, b) => {
      const oa = orderMap.has(a[0]) ? orderMap.get(a[0]) : Infinity;
      const ob = orderMap.has(b[0]) ? orderMap.get(b[0]) : Infinity;
      if (oa !== ob) return oa - ob;
      return a[0].localeCompare(b[0], 'vi');
    });
  }, [templates, search, categories]);

  const selected = templates.find(t => t.id === selectedId);

  // Helper: tính các field bắt buộc đang trống (visible only)
  const getMissingFields = () => {
    if (!selected) return [];
    return ensureSystemFields(selected.form_fields || []).filter(f => {
      if (!f.required) return false;
      if (f.is_system && f.enabled === false) return false;
      // Ô hệ tự điền lúc gửi (đơn tái ký HĐLĐ) + tiêu đề của đơn tái ký (máy chủ đặt «Tái ký hợp đồng — tên»).
      if (f.server_filled) return false;
      if (selected.kind === 'contract_renewal' && f.type === '_title') return false;
      if (FIELD_TYPE_BY_VALUE[f.type]?.isLayout) return false;
      // Ô chọn bản ghi CRM ở hệ tắt CRM — ẩn khỏi form (xem RequestForm), không bắt điền.
      if (isCrmFieldOff(f.type)) return false;
      if (!evaluateVisible(f.visible_when, formData)) return false;
      const v = formData[f.id];
      return v == null || v === '' || (Array.isArray(v) && v.length === 0);
    });
  };

  const missingFields = selected ? getMissingFields() : [];

  // Lượt gửi còn lại trong tháng (settings.submit_limit) — fetch khi chọn mẫu.
  // Fail-open ở FE (BE vẫn chặn cứng): lỗi fetch → không gate, chỉ mất banner.
  useEffect(() => {
    if (!selectedId) { setQuota(null); return; }
    let alive = true;
    setQuota(null);
    api.get(`/approvals/templates/${selectedId}/submit-quota`)
      .then(res => { if (alive) setQuota(res.data?.data || null); })
      .catch(() => { if (alive) setQuota(null); });
    return () => { alive = false; };
  }, [selectedId]);

  const limitBlocked = !!(quota && quota.ok === false);

  const handleSelect = (tpl) => {
    setSelectedId(tpl.id);
    setFormData(forceSelfAssignee(tpl, initialFormDataFor(tpl)));
  };

  const closeDrawer = () => { setSelectedId(null); setFormData({}); };

  // Đơn nghỉ CÓ LƯƠNG không đủ phép → LeaveBalanceCard đẩy marker vào form_data → chặn gửi.
  const leaveBlocked = Object.values(formData || {}).includes(LEAVE_INSUFFICIENT_MARKER);

  const handleSubmit = async () => {
    if (!selected) { toast.error(t`Hãy chọn loại đơn`); return; }

    if (leaveBlocked) {
      toast.error(t`Không đủ phép — không gửi được đơn nghỉ có lương. Chọn "Nghỉ không lương" hoặc giảm số ngày.`, { duration: 6000 });
      return;
    }

    if (limitBlocked) {
      toast.error(quota?.message || t`Đã đạt giới hạn số lần gửi trong tháng.`, { duration: 6000 });
      return;
    }

    // Validate visible required fields → set inline errors
    const errors = {};
    const missing = getMissingFields();
    missing.forEach(f => { errors[f.id] = t`${f.label || t`Trường này`} là bắt buộc`; });
    setFieldErrors(errors);

    if (missing.length > 0) {
      const names = missing.slice(0, 3).map(f => `"${f.label}"`).join(', ');
      const more = missing.length > 3 ? t` và ${missing.length - 3} trường khác` : '';
      toast.error(t`Vui lòng điền: ${names}${more}`, { duration: 5000 });
      // Auto-scroll tới field lỗi đầu tiên
      setTimeout(() => {
        const el = document.querySelector(`[data-field-id="${missing[0].id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    // Ngày vô lý (năm gõ thiếu chữ số ⇒ 0026, «đến» trước «từ») — câu đỏ đã hiện ngay dưới ô
    // (builder/DateIssueHint), ở đây chỉ chặn gửi + đưa tới ô đầu tiên. Máy chủ chặn lần nữa (kernel approvalFormDates).
    const dateIssues = findDateIssues(ensureSystemFields(selected.form_fields || []), formData);
    if (dateIssues.length > 0) {
      const names = dateIssues.slice(0, 3).map(({ field }) => `"${field.label}"`).join(', ');
      toast.error(t`Ngày chưa hợp lệ ở ${names} — sửa theo dòng báo đỏ dưới ô rồi gửi lại.`, { duration: 6000 });
      setTimeout(() => {
        const el = document.querySelector(`[data-field-id="${dateIssues[0].field.id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    setSubmitting(true);
    try {
      const priority = formData._priority || 'normal';
      // Chốt lần cuối trước khi POST — state có thể mang giá trị cũ nếu admin vừa bật khoá.
      const payload = forceSelfAssignee(selected, formData);
      const res = await api.post('/approvals', { ...(submitExtra || {}), template_id: selected.id, form_data: payload, urgency: priority });
      toast.success(t`Đã gửi đơn`);
      setFieldErrors({});
      onSubmitted?.(res.data?.data?.id);
      closeDrawer();
      onClose?.();
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || t`Lỗi không xác định`;
      // Show explicit error
      const statusPrefix = status ? `[${status}] ` : '';
      toast.error(t`${statusPrefix}Gửi đơn thất bại: ${msg}`, { duration: 8000 });
      console.error('[approval-submit] error:', err.response?.data || err);
    }
    finally { setSubmitting(false); }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      {/* Fullscreen gallery */}
      <div className="fixed inset-0 z-[9990] bg-slate-50 overflow-auto overscroll-contain flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 shadow-sm shrink-0">
          <div className="px-5 h-14 flex items-center gap-3">
            <button onClick={onClose}
              className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition">
              <ArrowLeft size={16} /> <Trans>Quay lại</Trans>
            </button>
            <h1 className="text-sm font-semibold text-gray-900"><Trans>Chọn loại đơn để gửi</Trans></h1>
            <div className="flex-1" />
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t`Tìm loại đơn...`}
                className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none w-64" />
            </div>
          </div>
        </header>

        {/* Body — gallery */}
        <div className="flex-1">
          <div className="max-w-5xl mx-auto px-5 py-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2"><Trans>Bạn muốn gửi đơn gì?</Trans></h2>
              <p className="text-sm text-gray-500"><Trans>Chọn 1 mẫu để bắt đầu điền thông tin.</Trans></p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : error ? (
              <div className="text-center py-20 text-red-500">
                <AlertTriangle size={36} className="mx-auto mb-3 opacity-60" />
                <p className="font-medium mb-3">{error}</p>
                <button onClick={loadTemplates} className="text-sm px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl hover:bg-blue-100"><Trans>Thử lại</Trans></button>
              </div>
            ) : grouped.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <FileCheck size={36} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">{search ? t`Không tìm thấy loại đơn nào` : t`Chưa có mẫu đơn nào`}</p>
              </div>
            ) : (
              grouped.map(([cat, tpls]) => (
                <div key={cat} className="mb-8">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-1">{cat}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {tpls.map(tpl => {
                      const TIcon = getIconComponent(tpl.icon);
                      const tColor = getColorClass(tpl.icon_bg_color);
                      return (
                        <button key={tpl.id} onClick={() => handleSelect(tpl)}
                          className="group p-4 bg-white border border-gray-200 rounded-2xl hover:border-blue-400 hover:shadow-md transition text-left">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-xl ${tColor} flex items-center justify-center shrink-0 shadow-sm`}>
                              <TIcon size={20} className="text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 truncate">{tpl.name}</p>
                              {tpl.description && (
                                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{tpl.description}</p>
                              )}
                              {tpl.code != null && (
                                <p className="text-[10px] text-gray-400 font-mono mt-1.5">#{String(tpl.code).padStart(3, '0')}</p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Slide drawer with form */}
      <SlideDrawer open={!!selected} onClose={closeDrawer} width={640} headerHeight={48}
        title={selected ? (() => {
          const TIcon = getIconComponent(selected.icon);
          const tColor = getColorClass(selected.icon_bg_color);
          return (
            <span className="flex items-center gap-2">
              <span className={`w-7 h-7 rounded-lg ${tColor} flex items-center justify-center`}>
                <TIcon size={14} className="text-white" />
              </span>
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-bold text-gray-900">{selected.name}</span>
                {selected.category && <span className="text-[11px] text-gray-400 font-normal">{selected.category}</span>}
              </div>
            </span>
          );
        })() : null}
        footer={selected && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 text-xs text-gray-500">
              {limitBlocked ? (
                <span className="flex items-center gap-1.5 text-red-600">
                  <AlertTriangle size={13} />
                  <Trans>Đã đạt giới hạn</Trans> {quota.limit} <Trans>lượt/tháng</Trans>
                </span>
              ) : missingFields.length > 0 ? (
                <span className="flex items-center gap-1.5 text-amber-600">
                  <AlertTriangle size={13} />
                  <Trans>Còn</Trans> {missingFields.length} <Trans>trường bắt buộc chưa điền</Trans>
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-green-600">
                  <Trans>✓ Sẵn sàng gửi</Trans>
                </span>
              )}
            </div>
            <button onClick={closeDrawer} disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition disabled:opacity-50">
              <Trans>Huỷ</Trans>
            </button>
            <button onClick={handleSubmit} disabled={submitting || leaveBlocked || limitBlocked}
              title={limitBlocked ? (quota?.message || t`Đã đạt giới hạn số lần gửi trong tháng`)
                : leaveBlocked ? t`Không đủ phép — không gửi được đơn nghỉ có lương`
                : missingFields.length > 0 ? t`Còn ${missingFields.length} trường chưa điền` : t`Bấm để gửi đơn`}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 inline-flex items-center gap-1.5">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} <Trans>Gửi đơn</Trans>
            </button>
          </div>
        )}>
        {selected && (
          <div className="p-6">
            {selected.description && <p className="text-sm text-gray-500 mb-4">{selected.description}</p>}

            {/* Lượt gửi còn lại trong tháng (settings.submit_limit) */}
            {quota && !quota.unlimited && (
              <div className={`mb-4 p-3 rounded-xl border flex items-start gap-2.5 ${limitBlocked ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span className="text-sm">
                  {limitBlocked ? quota.message
                    : t`Bạn còn ${quota.remaining}/${quota.limit} lượt gửi loại đơn này trong tháng.`}
                </span>
              </div>
            )}

            {/* Validation summary banner — hiện khi có lỗi */}
            {Object.keys(fieldErrors).length > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5">
                <AlertTriangle size={16} className="text-red-500 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm">
                  <p className="font-medium text-red-700 mb-1"><Trans>Có</Trans> {Object.keys(fieldErrors).length} <Trans>trường chưa điền:</Trans></p>
                  <ul className="text-red-600 list-disc ml-4 space-y-0.5">
                    {Object.entries(fieldErrors).map(([fid, msg]) => (
                      <li key={fid}>{msg}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <RequestForm template={selected} formData={formData} setFormData={setFormData}
              fieldErrors={fieldErrors}
              onClearError={(fid) => setFieldErrors(prev => { const n = { ...prev }; delete n[fid]; return n; })} />
          </div>
        )}
      </SlideDrawer>
    </>,
    document.body
  );
}

function RequestForm({ template, formData, setFormData, fieldErrors = {}, onClearError }) {
  const { t } = useLingui();
  // Khoá theo LOẠI ĐƠN áp cả cho mẫu cũ chưa lưu lại (DB chưa có cờ) — không gắn thì ô
  // vẫn cho chọn người khác rồi bấm gửi mới ăn 400 từ máy chủ.
  const fields = withKindAssigneeLock(ensureSystemFields(template.form_fields || []), template.kind);
  return (
    <div className="space-y-4">
      {fields.length === 0 && <p className="text-sm text-gray-400 italic"><Trans>Mẫu này không có trường nào.</Trans></p>}

      {fields.map(rawField => {
        if (rawField.is_system && rawField.enabled === false) return null;
        // Ô chọn bản ghi CRM ở hệ TẮT CRM (Site Connect 12/10/2026): mẫu cũ còn ô loại này ⇒ ẩn, không picker /crm/*.
        if (isCrmFieldOff(rawField.type)) return null;
        if (!evaluateVisible(rawField.visible_when, formData)) return null;
        // Đơn tái ký HĐLĐ: 5 ô hồ sơ (vị trí, phòng ban, ngày vào làm, HĐ hiện tại, ngày hết hạn) máy chủ tự điền
        // lúc gửi — không cho gõ; tiêu đề để trống thì máy chủ tự đặt.
        if (rawField.server_filled) return null;
        const renewal = template.kind === 'contract_renewal';
        const field = renewal && rawField.type === '_title'
          ? { ...rawField, required: false, placeholder: t`Để trống — hệ thống tự đặt «Tái ký hợp đồng — tên nhân sự»` }
          : rawField;
        const isLayout = FIELD_TYPE_BY_VALUE[field.type]?.isLayout;
        const hasError = !!fieldErrors[field.id];
        return (
          <div key={field.id} data-field-id={field.id}
            className={hasError ? 'p-3 -mx-3 rounded-xl bg-red-50/40 border border-red-200' : ''}>
            {!isLayout && field.type !== '_title' && (
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
            )}
            <FieldInput field={field} value={formData[field.id]}
              onChange={v => {
                setFormData(f => ({ ...f, [field.id]: v }));
                if (hasError) onClearError?.(field.id);
              }}
              formData={formData} formFields={fields} />
            {renewal && field._purpose === 'renewal_subject' && (
              <p className="text-xs text-gray-500 mt-1">
                <Trans>Vị trí, phòng ban, ngày vào làm và hợp đồng hiện tại hệ thống tự điền khi gửi đơn.</Trans>
              </p>
            )}
            {hasError && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertTriangle size={11} /> {fieldErrors[field.id]}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
